import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Save, Send, History } from "lucide-react";

const fechaAR = (iso) => (iso ? new Date(iso).toLocaleDateString("es-AR") : "—");

export default function TycTab() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const edRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/admin/tyc");
    setCfg(data);
    setEnabled(data.enabled);
    if (edRef.current && edRef.current.innerHTML !== (data.draftContent || "")) {
      edRef.current.innerHTML = data.draftContent || "";
    }
  };
  useEffect(() => { load().catch(() => toast.error("Error al cargar Términos y Condiciones")); }, []);

  const exec = (cmd, val) => {
    edRef.current?.focus();
    if (cmd === "createLink") {
      const url = window.prompt("URL del enlace:");
      if (!url) return;
      document.execCommand(cmd, false, url);
    } else document.execCommand(cmd, false, val);
  };

  const toggleEnabled = async (v) => {
    setEnabled(v);
    try {
      await api.put("/admin/tyc", { enabled: v });
      toast.success(v ? "Términos habilitados para todos los usuarios" : "Términos deshabilitados");
      load();
    } catch (e) { setEnabled(!v); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put("/admin/tyc", { content: edRef.current.innerHTML });
      toast.success("Borrador guardado (no afecta a los usuarios hasta publicar)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tyc/publish", { content: edRef.current.innerHTML });
      toast.success(`Versión v${data.version} publicada — todos los usuarios deberán aceptarla`);
      setConfirmOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-8 text-muted-foreground">Cargando…</div>;

  const tools = [
    ["bold", <Bold size={14} key="b" />, "Negrita"],
    ["italic", <Italic size={14} key="i" />, "Cursiva"],
    ["formatBlock-h2", <Heading2 size={14} key="h2" />, "Título"],
    ["formatBlock-h3", <Heading3 size={14} key="h3" />, "Subtítulo"],
    ["insertUnorderedList", <List size={14} key="ul" />, "Lista"],
    ["insertOrderedList", <ListOrdered size={14} key="ol" />, "Lista numerada"],
    ["createLink", <Link2 size={14} key="lk" />, "Enlace"],
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white border border-border p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={toggleEnabled} data-testid="tyc-enabled-switch" />
            <Label className="text-sm font-medium">Habilitado para todos los usuarios</Label>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="tyc-status-strip">
            Versión vigente: <b>{cfg.currentVersion > 0 ? `v${cfg.currentVersion}` : "—"}</b>
            {" · "}Publicada: <b>{fechaAR(cfg.publishedAt)}</b>
            {" · "}Aceptaron: <b>{cfg.acceptedCount} de {cfg.totalUsers}</b> usuarios
          </p>
        </div>
        {enabled && cfg.currentVersion === 0 && (
          <p className="text-xs text-amber-700 mt-2" data-testid="tyc-no-version-warning">
            ⚠ Aún no hay ninguna versión publicada: los usuarios no verán el aviso hasta que publiques con "Actualizar para todos".
          </p>
        )}
      </div>

      <div className="bg-white border border-border">
        <div className="flex items-center gap-1 border-b border-border p-2">
          {tools.map(([cmd, icon, title]) => (
            <button key={cmd} type="button" title={title} data-testid={`tyc-tool-${cmd}`}
              onClick={() => (cmd.startsWith("formatBlock-") ? exec("formatBlock", `<${cmd.split("-")[1]}>`) : exec(cmd))}
              className="h-8 w-8 flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-slate-900">
              {icon}
            </button>
          ))}
        </div>
        <div ref={edRef} contentEditable suppressContentEditableWarning data-testid="tyc-editor"
          className="min-h-[340px] max-h-[55vh] overflow-y-auto p-5 text-sm text-slate-700 leading-relaxed outline-none focus:ring-1 focus:ring-slate-300 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-600 [&_a]:underline" />
        <div className="flex items-center justify-between p-4 border-t border-border bg-slate-50">
          <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="tyc-save-draft"
            className="rounded-none gap-2"><Save size={15} /> Guardar borrador</Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="tyc-publish"
            className="rounded-none brand-bg text-white gap-2"><Send size={15} /> Actualizar para todos</Button>
        </div>
      </div>

      {cfg.versions?.length > 0 && (
        <div className="bg-white border border-border p-5" data-testid="tyc-history">
          <p className="label-tiny flex items-center gap-2 mb-3"><History size={14} /> Historial de versiones</p>
          <div className="space-y-2">
            {cfg.versions.map((v) => (
              <details key={v.id} className="border border-border">
                <summary className="cursor-pointer p-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  <b>v{v.version}</b> · publicada el {fechaAR(v.publishedAt)}
                  {v.version === cfg.currentVersion && <span className="ml-2 text-xs text-emerald-700">(vigente)</span>}
                </summary>
                <div className="p-4 border-t border-border text-sm text-slate-600 [&_h2]:font-bold [&_ul]:list-disc [&_ul]:pl-5"
                  dangerouslySetInnerHTML={{ __html: v.content }} />
              </details>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Actualizar para todos</AlertDialogTitle>
            <AlertDialogDescription>
              Se creará la versión v{cfg.currentVersion + 1} y <b>TODOS los usuarios</b> deberán volver a aceptar
              los Términos y Condiciones y la Política de Privacidad al ingresar. ¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="tyc-publish-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={publish} disabled={busy} className="rounded-none brand-bg text-white"
              data-testid="tyc-publish-confirm">Publicar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
